import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { tomTatRepository } from '../../../lib/repositories/van-ban-tom-tat.repository';
import { soanThaoRepository } from '../../../lib/repositories/van-ban-soan-thao.repository';
import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import type { KhoaDich } from '../../../i18n';
import { useToast } from '../../../lib/toast';
import { dangXuLy } from '../nghiep-vu/tom-tat';
import { KHOA_PHAN_CONG } from './usePhanCong';
import type { ThemTomTat, YeuCauSoan } from '../types-tom-tat';

/**
 * Hook của màn Tóm tắt và Soạn thảo văn bản. Tóm tắt chạy nền: còn văn bản chờ hoặc đang xử lý thì
 * danh sách hỏi lại mỗi 4 giây (ADR-0071), hết thì thôi hỏi.
 */
const KHOA = {
  ds: ['van-ban', 'tom-tat'] as const,
  mot: (id: string) => ['van-ban', 'tom-tat', id] as const,
};
const HOI_LAI_MS = 4_000;

function useGhi<V, R>(ham: (v: V) => Promise<R>, thongBao: (v: V) => KhoaDich | null, baoLoi = true) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { t } = useT();
  const dichLoi = useLoi();
  return useMutation({
    mutationFn: ham,
    onSuccess: (_d, v) => {
      void queryClient.invalidateQueries({ queryKey: KHOA.ds });
      const k = thongBao(v);
      if (k) toast.success(t(k));
    },
    onError: (err) => {
      if (baoLoi) toast.error(dichLoi(err));
    },
  });
}

// ─── Tóm tắt ─────────────────────────────────────────────────────────────────

export function useDanhSachTomTat() {
  return useQuery({
    queryKey: KHOA.ds,
    queryFn: tomTatRepository.danhSach,
    refetchInterval: (q) => (q.state.data?.data.some((d) => dangXuLy(d.status)) ? HOI_LAI_MS : false),
  });
}

export function useChiTietTomTat(id: string | null) {
  return useQuery({
    queryKey: KHOA.mot(id ?? '-'),
    queryFn: () => tomTatRepository.chiTiet(id!),
    enabled: !!id,
    refetchInterval: (q) => (q.state.data && dangXuLy(q.state.data.status) ? HOI_LAI_MS : false),
  });
}

/** Đọc chi tiết một lần (xuất tệp, báo lỗi tác vụ) mà không giữ một query mở. */
export function useDocTomTat() {
  const queryClient = useQueryClient();
  return (id: string) => queryClient.fetchQuery({ queryKey: KHOA.mot(id), queryFn: () => tomTatRepository.chiTiet(id), staleTime: 0 });
}

export function useThemTomTat() {
  return useGhi((v: ThemTomTat) => tomTatRepository.them(v), () => 'vanBanTomTat.daThem');
}

export function useChayTomTat() {
  return useGhi((id: string) => tomTatRepository.chay(id), () => null);
}

export function useSuaTomTat() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { t } = useT();
  const dichLoi = useLoi();
  return useMutation({
    mutationFn: (v: { id: string; summary: string }) => tomTatRepository.suaTomTat(v.id, v.summary),
    onSuccess: (d, v) => {
      queryClient.setQueryData(KHOA.mot(v.id), d);
      void queryClient.invalidateQueries({ queryKey: KHOA.ds, exact: true });
      toast.success(t('vanBanTomTat.daLuuTomTat'));
    },
    onError: (err) => toast.error(dichLoi(err)),
  });
}

export function useXoaTomTat() {
  return useGhi((id: string) => tomTatRepository.xoa(id), () => 'vanBanTomTat.daXoa');
}

/**
 * AI phân công từ văn bản đã tóm tắt (`POST /task-assignments/assign/{id}`); `409 VAN_BAN_STATUS_CONFLICT` khi văn
 * bản đã được phân công. Thành công không toast: hộp so sánh hiện dải xanh "Phân công thành công" như bản cũ. Làm mới
 * cả danh sách của màn Phân công.
 */
export function usePhanCongTuTomTat() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const dichLoi = useLoi();
  return useMutation({
    mutationFn: (id: string) => tomTatRepository.phanCong(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: KHOA.ds });
      void queryClient.invalidateQueries({ queryKey: KHOA_PHAN_CONG });
    },
    onError: (err) => toast.error(dichLoi(err)),
  });
}

// ─── Soạn thảo ───────────────────────────────────────────────────────────────

export function useSoanVanBan() {
  const toast = useToast();
  const dichLoi = useLoi();
  return useMutation({
    mutationFn: (v: YeuCauSoan) => soanThaoRepository.soan(v),
    onError: (err) => toast.error(dichLoi(err)),
  });
}

export function useDienTruongAi() {
  const toast = useToast();
  const dichLoi = useLoi();
  return useMutation({
    mutationFn: (v: { template_id: string; source_content: string; keys: string[] }) => soanThaoRepository.dienTruong(v),
    onError: (err) => toast.error(dichLoi(err)),
  });
}

/** Lỗi trả về chỗ gọi: màn hình gắn câu lỗi cạnh ô chọn tệp. */
export function useTrichChuTuTep() {
  return useMutation({ mutationFn: (tep: File) => soanThaoRepository.trichChu(tep) });
}
